import {
  Button,
  Col,
  ConfigProvider,
  Form,
  Input,
  Row,
  Select,
  Spin,
  Steps,
} from "antd";
import Summary from "./Summary";
import Payment from "./Payment";
import PageHeaderAllPage from "../../../Components/layouts/PageHeaderAllPage";
import Navbar from "../../Menu/Navbar";
import OrderCalendar from "./OrderCalendar";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../apis/store";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  toCreateAppointment,
  toGetAppointementSlot,
  toGetBranches,
} from "../../../apis/actions/appointments.actions";
import { showNotitfication } from "../../../utils/Utils";
import { useNavigate } from "react-router-dom";
import {
  setAmountToDisplay,
  setAppointment,
  setSelectedBranch,
  setDiscountAmount,
  setProducts,
  setServices,
  setShowPaiement,
} from "../../../apis/slices/globalSlice";
import Footer from "../Sections/Footer";
import { useTranslation } from "react-i18next";
import { toAddShop } from "../../../apis/actions/service.actions";
import { cancelCartAddedFeedback } from "../../../utils/cartAddedFeedback";
import { toGetModel } from "../../../apis/actions/vehicle.actions";
import { resolveVehicleModelDisplayName } from "../../../utils/vehicleDisplay";
import { buildSaudiPlateRows } from "../../../utils/plateConversions";
import { findBranchByName } from "../../../utils/branch";
import {
  parseShopCheckoutTotals,
  resolveOrderBaseAmount,
} from "../../../utils/paymentTotals";

type WizardStep = 1 | 2 | 3;

const Order = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { vehicle, services, products, my_cart, coupon, cartReady, cartLoading, cartError } = useSelector(
    (state: RootState) => state?.global
  );
  const cartSyncRef = useRef(my_cart);
  const couponSyncRef = useRef(coupon);
  const servicesSyncRef = useRef(services);
  const productsSyncRef = useRef(products);
  cartSyncRef.current = my_cart;
  couponSyncRef.current = coupon;
  servicesSyncRef.current = services;
  productsSyncRef.current = products;
  const { models } = useSelector((state: RootState) => state?.vehicle);

  const { AddShop } = useSelector((state: RootState) => state?.services);
  const isCartUpdating = AddShop?.loading === true || cartLoading;

  const { branches, slots } = useSelector(
    (state: RootState) => state?.appointmens
  );

  const [form] = Form.useForm();
  const [selected, setSelected] = useState<string | number>(-1);
  const [selectedSlot, setSelectedSlot] = useState<any>(null);
  const prevBranchRef = useRef<string | number>(-1);
  const [wizardStep, setWizardStep] = useState<WizardStep>(1);
  const skipStepScrollOnMount = useRef(true);


  useEffect(() => {
    if (skipStepScrollOnMount.current) {
      skipStepScrollOnMount.current = false;
      return;
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [wizardStep]);

  const vehicleModelLabel = useMemo(
    () => resolveVehicleModelDisplayName(vehicle, models?.list),
    [vehicle, models?.list]
  );

  const hasSelectedBranch = selected !== -1;

  useEffect(() => {
    const prev = prevBranchRef.current;
    if (prev !== -1 && selected !== -1 && prev !== selected) {
      setSelectedSlot(null);
    }
    prevBranchRef.current = selected;
  }, [selected]);

  useEffect(() => {
    if (wizardStep === 3 && !selectedSlot) {
      setWizardStep(2);
    }
  }, [selectedSlot, wizardStep]);

  useEffect(() => {
    if (wizardStep >= 2 && !hasSelectedBranch) {
      setWizardStep(1);
    }
  }, [hasSelectedBranch, wizardStep]);

  useEffect(() => {
    dispatch(toGetBranches(i18n.language));
  }, [dispatch]);

  useEffect(() => {
    dispatch(toGetModel());
  }, [dispatch]);

  useEffect(() => {
    dispatch(setAppointment(selectedSlot));
  }, [selectedSlot]);

  useEffect(() => {
    const branch = findBranchByName(branches?.list, selected);
    if (branch?.id != null) {
      dispatch(toGetAppointementSlot({ branch_id: branch.id }));
    }
  }, [selected, branches?.list, dispatch]);

  const toAddAppointment = async () => {
    if (!selectedSlot) {
      showNotitfication(t("profile.order.selectSlotError"), "error");
      return;
    }
    const branch = findBranchByName(branches?.list, selected);
    if (branch?.company_id == null) {
      showNotitfication(t("profile.order.branchPlaceholder"), "error");
      return;
    }
    const json = {
      vehicle_id: vehicle.id,
      appointment_slot_id: selectedSlot?.id,
      company_id: branch.company_id,
      service_ids: services.map((item: any) => item.id),
    };
    const success = (await dispatch(toCreateAppointment(json))) as any;
    const response = success?.payload?.data?.result;

    if (response?.code === 200) {
      showNotitfication(t("profile.order.appointmentAddedSuccess"), "success");
      setTimeout(() => {
        navigate("/profile");
        dispatch(setServices([]));
        dispatch(setProducts([]));
      }, 2800);
    } else {
      showNotitfication(response?.message, "error");
    }
  };
  useEffect(() => {
    const label =
      vehicleModelLabel || vehicle?.title || vehicle?.model_id || "";
    form.setFieldsValue({ car: label });
  }, [form, vehicle, vehicleModelLabel]);
  /** Single `shop` sync when entering payment step (after branch + slot), not on every cart edit. */
  useEffect(() => {
    if (wizardStep !== 3) return;
    const mc = cartSyncRef.current;
    if (!mc?.cart_id) return;

    let cancelled = false;
    (async () => {
      try {
        const payload = await dispatch(
          toAddShop({
            ...mc,
            coupon: couponSyncRef.current || null,
          })
        ).unwrap();
        if (cancelled) return;
        const result = payload?.data?.result;
        if (result) {
          const totals = parseShopCheckoutTotals(result);
          if (totals) {
            const orderAmount = resolveOrderBaseAmount(
              totals.orderAmount,
              servicesSyncRef.current,
              productsSyncRef.current,
              totals.discountAmount
            );
            dispatch(setAmountToDisplay(orderAmount));
            dispatch(
              setDiscountAmount(
                totals.discountAmount > 0
                  ? totals.discountAmount.toFixed(2)
                  : 0
              )
            );
          }
        }
      } catch (err) {
        cancelCartAddedFeedback();
        console.error("toAddShop failed:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [wizardStep, dispatch]);

  const orderPlateText = useMemo(() => {
    if (!vehicle) return "";
    const rows = buildSaudiPlateRows(vehicle.plate_numbers, {
      plateLetters: vehicle.plate_letters ?? undefined,
      letter1: (vehicle as { letter1?: string }).letter1,
      letter2: (vehicle as { letter2?: string }).letter2,
      letter3: (vehicle as { letter3?: string }).letter3,
    });
    const ar = i18n.language === "ar";
    const nums = ar ? rows.arNums : rows.enNums;
    const letters = ar ? rows.arLetters : rows.enLetters;
    return [nums, letters].join(" ").replace(/\s+/g, " ").trim();
  }, [vehicle, i18n.language]);

  const stepItems = useMemo(
    () => [
      { title: t("profile.order.step1") },
      { title: t("profile.order.step2") },
      { title: t("profile.order.step3") },
    ],
    [t]
  );

  const onStepsChange = (index: number) => {
    const target = (index + 1) as WizardStep;
    if (target === 1) {
      setWizardStep(1);
      return;
    }
    if (target === 2 && hasSelectedBranch) {
      setWizardStep(2);
      return;
    }
    if (target === 3 && hasSelectedBranch && selectedSlot) {
      setWizardStep(3);
    }
  };

  const goNext = async () => {
    if (!cartReady || cartLoading || cartError) return;
    if (wizardStep === 1) {
      try {
        await form.validateFields(["branch", "car"]);
        setWizardStep(2);
      } catch {
        /* validation messages */
      }
      return;
    }
    if (wizardStep === 2) {
      if (!selectedSlot) {
        showNotitfication(t("profile.order.selectSlotError"), "error");
        return;
      }
      setWizardStep(3);
      return;
    }
  };

  const goBack = () => {
    setWizardStep((s) => (s > 1 ? ((s - 1) as WizardStep) : s));
  };

  return (
    <>
      <Navbar type="internal" />
      <main className="main">
        <PageHeaderAllPage
          tone="brand"
          showMenu
          current={t("profile.order.title")}
          banner="assets/imgs/page-header/car1.png"
          link=""
        />

        <section className="ac--container-order ac-order-page">
          <ConfigProvider
            theme={{
              token: {
                colorPrimary: "var(--ac-theme-highlight)",
              },
            }}
          >
            <div className="container ac-order-page__inner">
              <p className="ac-order-page__lead">{t("profile.order.pageLead")}</p>

              <div className="ac-order-page__loading-shell">
                {isCartUpdating ? (
                  <div
                    className="ac-order-page__loading-overlay"
                    aria-busy="true"
                    aria-live="polite"
                  >
                    <Spin size="large" tip={t("profile.order.updatingOrder")} />
                  </div>
                ) : null}

                <div
                  className="ac-order-stepper-wrap"
                  role="navigation"
                  aria-label={t("profile.order.stepsAria")}
                >
                  <Steps
                    className="ac-order-ant-steps"
                    current={wizardStep - 1}
                    items={stepItems}
                    onChange={onStepsChange}
                    size="small"
                  />
                </div>

                <Row className="ac-order-page__row" gutter={[32, 32]}>
                  <Col
                    className="ac-order-page__main"
                    xxl={16}
                    xl={16}
                    md={14}
                    lg={14}
                    xs={24}
                    sm={24}
                  >
                {wizardStep === 1 ? (
                  <>
                    <div className="ac-order-panel ac-order-panel--accent">
                      <header className="ac-order-panel__head">
                        <div className="ac-order-panel__title-row">
                          <span className="ac-order-panel__step-num" aria-hidden>
                            1
                          </span>
                          <div className="ac-order-panel__title-stack">
                            <h2 className="ac-order-panel__title ac--font-expo">
                              {t("profile.order.sectionBooking")}
                            </h2>
                            <p className="ac-order-panel__lede">
                              {t("profile.order.sectionBookingHelp")}
                            </p>
                          </div>
                        </div>
                      </header>
                      <Form
                        form={form}
                        layout="vertical"
                        className="ac-order-panel__form"
                        requiredMark={false}
                      >
                        <Row gutter={[16, 8]}>
                          <Col xxl={12} xl={12} md={12} lg={12} xs={24} sm={24}>
                            <Form.Item
                              label={t("profile.order.branch")}
                              name="branch"
                              labelCol={{ className: "ac--order-item-title" }}
                              rules={[
                                {
                                  required: true,
                                },
                              ]}
                            >
                              <Select
                                onChange={(value) => {
                                  setSelected(value);
                                  const branch = findBranchByName(
                                    branches?.list,
                                    value
                                  );
                                  if (
                                    branch?.id != null &&
                                    branch?.company_id != null
                                  ) {
                                    dispatch(
                                      setSelectedBranch({
                                        branch_id: Number(branch.id),
                                        company_id: Number(branch.company_id),
                                      })
                                    );
                                  }
                                }}
                                className="ac--select"
                                placeholder={t(
                                  "profile.order.branchPlaceholder"
                                )}
                                optionFilterProp="label"
                                options={branches?.list.map((option: any) => ({
                                  label: option.name,
                                  value: option.name,
                                }))}
                                showSearch
                                getPopupContainer={(triggerNode) =>
                                  triggerNode.parentNode
                                }
                              />
                            </Form.Item>
                          </Col>
                          <Col xxl={12} xl={12} md={12} lg={12} xs={24} sm={24}>
                            <Form.Item
                              label={t("profile.car.plate")}
                              labelCol={{ className: "ac--order-item-title" }}
                            >
                              <Form.Item
                                name="car"
                                noStyle
                                rules={[{ required: true }]}
                              >
                                <Input type="hidden" />
                              </Form.Item>
                              {vehicle ? (
                                <div
                                  className="ac-order-vehicle-plate ac-order-vehicle-plate--text-only"
                                  dir={
                                    i18n.language === "ar" ? "rtl" : "ltr"
                                  }
                                  tabIndex={0}
                                  aria-label={`${t("profile.car.plate")}: ${orderPlateText}`}
                                >
                                  <span className="ac-order-vehicle-plate__value">
                                    {orderPlateText}
                                  </span>
                                </div>
                              ) : null}
                            </Form.Item>
                          </Col>
                        </Row>
                      </Form>
                    </div>
                    <div className="ac-order-step-footer">
                      <Button
                        type="primary"
                        size="large"
                        className="ac-order-step-footer__next"
                        disabled={!cartReady || cartLoading || Boolean(cartError)}
                        onClick={() => void goNext()}
                      >
                        {t("profile.order.stepNext")}
                      </Button>
                    </div>
                  </>
                ) : null}

                {wizardStep === 2 ? (
                  <>
                    <OrderCalendar
                      hasSelectedBranch={hasSelectedBranch}
                      selectedSlot={selectedSlot}
                      setSelectedSlot={(slot) => setSelectedSlot(slot)}
                      slots={slots}
                    />
                    <div className="ac-order-step-footer ac-order-step-footer--split">
                      <Button size="large" onClick={goBack}>
                        {t("profile.order.stepBack")}
                      </Button>
                      <Button
                        type="primary"
                        size="large"
                        className="ac-order-step-footer__next"
                        disabled={!cartReady || cartLoading || Boolean(cartError)}
                        onClick={() => void goNext()}
                      >
                        {t("profile.order.stepNext")}
                      </Button>
                    </div>
                  </>
                ) : null}

                {wizardStep === 3 ? (
                  <>
                    <div className="ac-order-panel ac-order-panel--accent ac-order-panel--review">
                      <header className="ac-order-panel__head">
                        <div className="ac-order-panel__title-row">
                          <span className="ac-order-panel__step-num" aria-hidden>
                            3
                          </span>
                          <div className="ac-order-panel__title-stack">
                            <h2 className="ac-order-panel__title ac--font-expo">
                              {t("profile.order.step3IntroTitle")}
                            </h2>
                            <p className="ac-order-panel__lede">
                              {t("profile.order.step3IntroLead")}
                            </p>
                          </div>
                        </div>
                      </header>
                    </div>
                    <div className="ac-order-panel ac-order-panel--accent ac-order-panel--payment-step">
                      <div className="ac-order-step-payment">
                        <Payment
                          btnTitle={t("profile.order.cart.payment")}
                          toSave={() => {
                            dispatch(setShowPaiement(true));
                          }}
                        />
                      </div>
                    </div>
                    <div className="ac-order-step-footer">
                      <Button size="large" onClick={goBack}>
                        {t("profile.order.stepBack")}
                      </Button>
                    </div>
                  </>
                ) : null}
                  </Col>

                  <Col
                    className="ac-order-page__aside"
                    xxl={8}
                    xl={8}
                    md={10}
                    lg={10}
                    xs={24}
                    sm={24}
                  >
                    <Summary
                      car={
                        vehicleModelLabel ||
                        vehicle?.title ||
                        String(vehicle?.model_id ?? "")
                      }
                      branch={selected}
                      slot={selectedSlot}
                      wizardStep={wizardStep}
                    />
                  </Col>
                </Row>
              </div>
            </div>
          </ConfigProvider>
        </section>
      </main>
      <Footer type="internal" />
    </>
  );
};

export default Order;
